/**
 * Enhanced Interview Results Page
 * Main page for displaying enhanced evaluation results with video background and radar charts
 */

import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { 
  Box, 
  ThemeProvider, 
  createTheme, 
  AppBar, 
  Toolbar, 
  Typography, 
  IconButton,
  Button,
  Alert,
  Tabs,
  Tab,
  Paper,
  CircularProgress
} from '@mui/material';
import { 
  ArrowBack as ArrowBackIcon,
  Download as DownloadIcon,
  Refresh as RefreshIcon 
} from '@mui/icons-material';
import ReactPlayer from 'react-player';
import { Radar } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  RadialLinearScale,
  PointElement,
  LineElement,
  Filler,
  Tooltip,
  Legend,
} from 'chart.js';

// Import enhanced utilities
import { getEnhancedEvaluationData } from '../utils/enhancedEvaluationStorage';
import { 
  getEnhancedRadarChartData, 
  getEnhancedAIScore, 
  EnhancedEvaluationSummary 
} from '../components/EnhancedEvaluationInterface';

// Register Chart.js components
ChartJS.register(
  RadialLinearScale,
  PointElement,
  LineElement,
  Filler,
  Tooltip,
  Legend
);

// Use existing dark theme from CompanyCandidateViewProfilev1
const theme = createTheme({
  palette: {
    mode: "dark",
    primary: {
      main: "#9900FF",
    },
    background: {
      default: "#121212",
      paper: "rgba(18, 18, 18, 0.8)"
    }
  },
});

const EnhancedInterviewResults = () => {
  const { interviewSlotId } = useParams();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [evaluationData, setEvaluationData] = useState(null);
  const [activeTab, setActiveTab] = useState(0);
  const [videoUrl, setVideoUrl] = useState(null);

  useEffect(() => {
    loadInterviewData();
  }, [interviewSlotId]);

  const loadInterviewData = async () => {
    try {
      setLoading(true);
      setError(null);

      // Load enhanced evaluation data
      const enhancedData = getEnhancedEvaluationData(interviewSlotId);
      
      if (!enhancedData.hasData) {
        throw new Error(`No interview data found for slot ID: ${interviewSlotId}`);
      }

      setEvaluationData(enhancedData);

      // For demo: Check if video URL exists in localStorage or create sample
      const videoKey = `interview_video_${interviewSlotId}`;
      const storedVideo = localStorage.getItem(videoKey);
      if (storedVideo) {
        setVideoUrl(storedVideo);
      }

    } catch (err) {
      console.error('Error loading interview data:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleExportData = () => {
    if (!evaluationData) return;

    const exportData = {
      interviewSlotId,
      timestamp: new Date().toISOString(),
      evaluation: evaluationData.adaptedEvaluation,
      format_info: evaluationData.format_info,
      metadata: evaluationData.metadata
    };

    const blob = new Blob([JSON.stringify(exportData, null, 2)], { 
      type: 'application/json' 
    });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `interview_results_${interviewSlotId}_${Date.now()}.json`;
    link.click();
    URL.revokeObjectURL(url);
  };

  if (loading) {
    return (
      <ThemeProvider theme={theme}>
        <Box sx={{ 
          display: 'flex', 
          justifyContent: 'center', 
          alignItems: 'center', 
          height: '100vh',
          bgcolor: 'background.default'
        }}>
          <CircularProgress size={60} />
          <Typography sx={{ ml: 2 }}>Loading interview data...</Typography>
        </Box>
      </ThemeProvider>
    );
  }

  if (error) {
    return (
      <ThemeProvider theme={theme}>
        <Box sx={{ p: 3, bgcolor: 'background.default', minHeight: '100vh' }}>
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
          <Typography variant="body2" sx={{ mb: 2, opacity: 0.7 }}>
            Make sure you have evaluation data stored for interview slot: {interviewSlotId}
          </Typography>
          <Button onClick={loadInterviewData} startIcon={<RefreshIcon />}>
            Retry
          </Button>
        </Box>
      </ThemeProvider>
    );
  }

  // Create mock interview evaluations array for compatibility
  const mockInterviewEvaluations = evaluationData?.adaptedEvaluation ? [{
    evaluation: evaluationData.adaptedEvaluation
  }] : [];

  return (
    <ThemeProvider theme={theme}>
      <Box sx={{ bgcolor: 'background.default', minHeight: '100vh' }}>
        {/* Header */}
        <AppBar
          position="fixed"
          sx={{
            bgcolor: "rgba(0,0,0,0.6)",
            backdropFilter: "blur(8px)",
            boxShadow: "none",
            borderBottom: "1px solid rgba(153, 0, 255, 0.3)",
            zIndex: 1300
          }}
        >
          <Toolbar>
            <IconButton
              edge="start"
              color="inherit"
              aria-label="back"
              onClick={() => window.history.back()}
              sx={{ mr: 2 }}
            >
              <ArrowBackIcon />
            </IconButton>
            <Typography variant="h6" component="div" sx={{ flexGrow: 1 }}>
              Enhanced Interview Results
            </Typography>
            <Typography variant="body2" sx={{ mr: 2, opacity: 0.7 }}>
              Slot: {interviewSlotId}
            </Typography>
            <Button
              color="inherit"
              startIcon={<DownloadIcon />}
              onClick={handleExportData}
            >
              Export
            </Button>
          </Toolbar>
        </AppBar>

        {/* Main Content */}
        <Box sx={{ pt: 8 }}>
          {/* Format Information */}
          {evaluationData?.format_info && (
            <Alert severity="success" sx={{ m: 2 }}>
              Format: {evaluationData.format_info.original_format} → standardized_v2 
              {evaluationData.format_info.validation_score && 
                ` (Validation: ${evaluationData.format_info.validation_score}%)`
              }
            </Alert>
          )}

          {/* Video Player Section (if video exists) */}
          {videoUrl && (
            <Box sx={{ 
              position: 'relative', 
              height: '50vh', 
              mb: 3,
              border: '1px solid rgba(153, 0, 255, 0.3)',
              borderRadius: 1,
              overflow: 'hidden'
            }}>
              <ReactPlayer
                url={videoUrl}
                width="100%"
                height="100%"
                controls={true}
                style={{
                  backgroundColor: 'black'
                }}
              />
            </Box>
          )}

          {/* Tabs for Different Views */}
          <Paper sx={{ 
            bgcolor: 'rgba(0,0,0,0.2)', 
            border: '1px solid rgba(153, 0, 255, 0.1)',
            borderRadius: 2,
            mx: 2
          }}>
            <Tabs 
              value={activeTab} 
              onChange={(e, newValue) => setActiveTab(newValue)}
              centered
              sx={{
                '& .MuiTab-root': { 
                  color: 'rgba(255,255,255,0.7)',
                  '&:hover': {
                    color: 'rgba(255,255,255,0.9)'
                  }
                },
                '& .Mui-selected': { 
                  color: 'primary.main',
                  fontWeight: 'bold'
                },
                '& .MuiTabs-indicator': {
                  backgroundColor: 'primary.main'
                }
              }}
            >
              <Tab label="Radar Chart & Score" />
              <Tab label="Detailed Evaluation" />
              <Tab label="Raw Data" />
            </Tabs>

            {/* Tab Content */}
            <Box sx={{ p: 3 }}>
              {/* Radar Chart Tab */}
              {activeTab === 0 && (
                <Box>
                  <Typography variant="h5" gutterBottom color="primary" textAlign="center">
                    Skills Assessment Overview
                  </Typography>
                  
                  {mockInterviewEvaluations.length > 0 ? (
                    <>
                      {/* AI Score Display */}
                      <Box sx={{ mb: 4, textAlign: 'center' }}>
                        <Typography variant="h2" color="primary">
                          {getEnhancedAIScore(mockInterviewEvaluations)}%
                        </Typography>
                        <Typography variant="h6" color="textSecondary">
                          Overall AI Assessment Score
                        </Typography>
                        {evaluationData.adaptedEvaluation.recommendation && (
                          <Typography variant="h6" color="primary" sx={{ mt: 1 }}>
                            Recommendation: {evaluationData.adaptedEvaluation.recommendation}
                          </Typography>
                        )}
                      </Box>

                      {/* Radar Chart */}
                      <Box sx={{ 
                        display: 'flex', 
                        justifyContent: 'center',
                        alignItems: 'center',
                        height: '500px',
                        mb: 3,
                        width: '100%'
                      }}>
                        <Box sx={{ 
                          width: '480px', 
                          height: '480px',
                          position: 'relative'
                        }}>
                          <Radar
                            data={getEnhancedRadarChartData(mockInterviewEvaluations)}
                            options={{
                              responsive: true,
                              maintainAspectRatio: false,
                              layout: {
                                padding: {
                                  top: 60,
                                  right: 60,
                                  bottom: 60,
                                  left: 60
                                }
                              },
                              scales: {
                                r: {
                                  beginAtZero: true,
                                  max: 10,
                                  ticks: {
                                    stepSize: 2,
                                    showLabelBackdrop: false,
                                    color: "rgba(255, 255, 255, 0.7)",
                                    font: { size: 10 }
                                  },
                                  grid: { color: "rgba(255, 255, 255, 0.1)" },
                                  angleLines: { color: "rgba(255, 255, 255, 0.1)" },
                                  pointLabels: {
                                    color: "rgba(255, 255, 255, 0.9)",
                                    font: { size: 14, weight: "bold" },
                                    padding: 30,
                                    centerPointLabels: true,
                                    callback: function(label) {
                                      // Enhanced label handling for better display
                                      if (label.length > 12) {
                                        const words = label.split(' ');
                                        if (words.length > 1) {
                                          const mid = Math.ceil(words.length / 2);
                                          return [
                                            words.slice(0, mid).join(' '),
                                            words.slice(mid).join(' ')
                                          ];
                                        } else if (label.length > 18) {
                                          const mid = Math.floor(label.length / 2);
                                          return [label.slice(0, mid), label.slice(mid)];
                                        }
                                      }
                                      return label;
                                    }
                                  }
                                }
                              },
                              plugins: {
                                legend: {
                                  position: 'bottom',
                                  labels: { 
                                    color: "rgba(255, 255, 255, 0.9)",
                                    padding: 20,
                                    font: { size: 12 }
                                  }
                                },
                                tooltip: {
                                  backgroundColor: 'rgba(0, 0, 0, 0.8)',
                                  titleColor: 'white',
                                  bodyColor: 'white',
                                  borderColor: 'rgba(153, 0, 255, 1)',
                                  borderWidth: 1
                                }
                              }
                            }}
                          />
                        </Box>
                      </Box>

                      {/* Quick Stats */}
                      <Box sx={{ 
                        display: 'grid', 
                        gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', 
                        gap: 2,
                        mt: 3
                      }}>
                        {[
                          { label: 'Technical', value: evaluationData.adaptedEvaluation.technical_skills },
                          { label: 'Communication', value: evaluationData.adaptedEvaluation.communication_skills },
                          { label: 'Problem Solving', value: evaluationData.adaptedEvaluation.problem_solving },
                          { label: 'Leadership', value: evaluationData.adaptedEvaluation.leadership }
                        ].map((skill, index) => (
                          <Paper key={index} sx={{ 
                            p: 2, 
                            textAlign: 'center',
                            bgcolor: 'rgba(0,0,0,0.3)',
                            border: '1px solid rgba(153, 0, 255, 0.1)'
                          }}>
                            <Typography variant="h4" color="primary">
                              {skill.value}%
                            </Typography>
                            <Typography variant="body2" color="textSecondary">
                              {skill.label}
                            </Typography>
                          </Paper>
                        ))}
                      </Box>
                    </>
                  ) : (
                    <Alert severity="warning">
                      No evaluation data available for radar chart
                    </Alert>
                  )}
                </Box>
              )}

              {/* Detailed Evaluation Tab */}
              {activeTab === 1 && (
                <EnhancedEvaluationSummary 
                  interviewEvaluations={mockInterviewEvaluations}
                />
              )}

              {/* Raw Data Tab */}
              {activeTab === 2 && (
                <Box>
                  <Typography variant="h6" gutterBottom>
                    Raw Evaluation Data
                  </Typography>
                  <Paper sx={{ 
                    p: 2, 
                    bgcolor: 'rgba(0,0,0,0.4)', 
                    border: '1px solid rgba(153, 0, 255, 0.1)',
                    maxHeight: '600px',
                    overflow: 'auto',
                    borderRadius: 1
                  }}>
                    <pre style={{ 
                      color: 'rgba(255,255,255,0.9)', 
                      fontSize: '11px',
                      lineHeight: '1.4',
                      margin: 0,
                      fontFamily: 'Monaco, Consolas, "Courier New", monospace'
                    }}>
                      {JSON.stringify(evaluationData, null, 2)}
                    </pre>
                  </Paper>
                </Box>
              )}
            </Box>
          </Paper>

          {/* Footer space */}
          <Box sx={{ height: '50px' }} />
        </Box>
      </Box>
    </ThemeProvider>
  );
};

export default EnhancedInterviewResults;